/**
 * Field value renderers for Filevine collection data.
 * Renders field values based on their custom_field_type.
 */

import { format, parseISO } from 'date-fns';
import {
  AlertCircle,
  Calendar,
  Check,
  DollarSign,
  FileText,
  Link as LinkIcon,
  Mail,
  Phone,
  User,
  X,
} from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { CustomFieldType } from '@/services/api/entitySyncService';
import {
  type AttachedDocument,
  type ComputedFieldValue,
  type ProjectClientSchema,
} from '@/services/api/generated/entitySyncService';

import {
  isAttachmentArray,
  isComputedValue,
  isPersonValue,
} from './typeGuards';

// ============================================================================
// Component Props Types
// ============================================================================

interface FieldValueProps {
  value: unknown;
  fieldType: number;
  className?: string;
}

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format a date string for display
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const date = parseISO(dateStr);
    return format(date, 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

/**
 * Format a datetime string for display
 */
function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const date = parseISO(dateStr);
    return format(date, 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
}

/**
 * Format a currency value
 */
function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

/**
 * Format a percentage value
 */
function formatPercentage(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return `${num.toFixed(1)}%`;
}

// ============================================================================
// Base Value Renderers
// ============================================================================

/**
 * Render an empty/null value placeholder
 */
const EmptyValue: React.FC = () => <span className="text-zinc-400">—</span>;

/**
 * Render a text field value
 */
const TextValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }
  const text = String(value);
  return (
    <span className="text-zinc-900" title={text}>
      {text}
    </span>
  );
};

/**
 * Render a multiline text field value
 */
const MultilineTextValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }
  const text = String(value);
  return (
    <span
      className="text-zinc-900 whitespace-pre-wrap line-clamp-2"
      title={text}
    >
      {text}
    </span>
  );
};

/**
 * Render a date field value
 */
const DateValue: React.FC<{ value: unknown }> = ({ value }) => {
  const formatted = formatDate(value as string);
  if (formatted === '—') {
    return <EmptyValue />;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-zinc-900">
      <Calendar className="h-3.5 w-3.5 text-zinc-400" />
      {formatted}
    </span>
  );
};

/**
 * Render a datetime field value
 */
const DateTimeValue: React.FC<{ value: unknown }> = ({ value }) => {
  const formatted = formatDateTime(value as string);
  if (formatted === '—') {
    return <EmptyValue />;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-zinc-900">
      <Calendar className="h-3.5 w-3.5 text-zinc-400" />
      {formatted}
    </span>
  );
};

/**
 * Render a currency field value
 */
const CurrencyValue: React.FC<{ value: unknown }> = ({ value }) => {
  const formatted = formatCurrency(value as number | string);
  if (formatted === '—') {
    return <EmptyValue />;
  }
  return (
    <span className="inline-flex items-center gap-1 text-zinc-900 font-medium">
      <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
      {formatted.replace('$', '')}
    </span>
  );
};

/**
 * Render a percentage field value
 */
const PercentageValue: React.FC<{ value: unknown }> = ({ value }) => {
  const formatted = formatPercentage(value as number | string);
  if (formatted === '—') {
    return <EmptyValue />;
  }
  return <span className="text-zinc-900">{formatted}</span>;
};

/**
 * Render a boolean field value
 */
const BooleanValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }
  const isTrue = value === true || value === 'true' || value === 1;
  return isTrue ? (
    <span className="inline-flex items-center gap-1 text-emerald-600">
      <Check className="h-4 w-4" />
      Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-zinc-400">
      <X className="h-4 w-4" />
      No
    </span>
  );
};

/**
 * Render a dropdown field value as a badge
 */
const DropdownValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }
  return (
    <Badge variant="secondary" className="font-normal">
      {String(value)}
    </Badge>
  );
};

/**
 * Render a multi-select field value as badges
 */
const MultiSelectValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }

  const items = Array.isArray(value) ? value : [value];
  if (items.length === 0) {
    return <EmptyValue />;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, idx) => (
        <Badge key={idx} variant="secondary" className="font-normal">
          {String(item)}
        </Badge>
      ))}
    </div>
  );
};

/**
 * Render a string list field value
 */
const StringListValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }

  const items = Array.isArray(value) ? value : [value];
  if (items.length === 0) {
    return <EmptyValue />;
  }

  const text = items.map(String).join(', ');
  return (
    <span className="text-zinc-900" title={text}>
      {text}
    </span>
  );
};

// ============================================================================
// Complex Value Renderers
// ============================================================================

/**
 * Render a person field value with contact info.
 * Handles ProjectClientSchema from Filevine collection data.
 */
const PersonValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }

  // Validate it's a proper person value using type guard
  if (!isPersonValue(value)) {
    // Fallback for unexpected shape - try to extract a displayable name
    const unknownValue = value as { fullName?: string; name?: string };
    const displayName =
      typeof value === 'object' && value !== null
        ? unknownValue.fullName || unknownValue.name || 'Unknown'
        : String(value);
    return (
      <span className="inline-flex items-center gap-1.5 text-zinc-900">
        <User className="h-3.5 w-3.5 text-zinc-400" />
        {displayName}
      </span>
    );
  }

  const person: ProjectClientSchema = value as ProjectClientSchema;
  const primaryPhone = person.phones?.[0];
  const primaryEmail = person.emails?.[0];

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-zinc-900 font-medium">
        <User className="h-3.5 w-3.5 text-zinc-500" />
        {person.fullname}
      </div>
      {primaryPhone && (
        <div className="text-xs text-zinc-500 flex items-center gap-1.5 pl-5">
          <Phone className="h-3 w-3" />
          {primaryPhone.number}
        </div>
      )}
      {primaryEmail && (
        <div className="text-xs text-zinc-500 flex items-center gap-1.5 pl-5">
          <Mail className="h-3 w-3" />
          {primaryEmail.address}
        </div>
      )}
    </div>
  );
};

/**
 * Render a project link field value
 */
const ProjectLinkValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }

  const unknownValue = value as { projectName?: string; name?: string };
  const displayValue =
    typeof value === 'object' && value !== null
      ? unknownValue.projectName || unknownValue.name || 'Linked Project'
      : String(value);

  return (
    <span className="inline-flex items-center gap-1.5 text-blue-600">
      <LinkIcon className="h-3.5 w-3.5" />
      {displayValue}
    </span>
  );
};

/**
 * Render a computed field value (auto-calculated by Filevine)
 * Shows value or error state
 */
const ComputedValueRenderer: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }

  // Validate it's a proper computed value using type guard
  if (!isComputedValue(value)) {
    return <TextValue value={value} />;
  }

  const computed: ComputedFieldValue = value;

  // Show error state if present
  if (computed.error) {
    return (
      <span className="inline-flex items-center gap-1 text-amber-600">
        <AlertCircle className="h-3.5 w-3.5" />
        {computed.error}
      </span>
    );
  }

  // Format the computed value (often currency)
  if (computed.value !== null && computed.value !== undefined) {
    const formatted = formatCurrency(computed.value);
    return (
      <span className="inline-flex items-center gap-1 text-zinc-900 font-medium">
        <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
        {formatted.replace('$', '')}
      </span>
    );
  }

  return <EmptyValue />;
};

/**
 * Render an attachment/document field value
 * Shows list of attached files with filenames
 */
const AttachmentValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }

  // Check for empty array
  if (Array.isArray(value) && value.length === 0) {
    return <span className="text-zinc-400 italic">No attachments</span>;
  }

  // Validate it's an attachment array using type guard
  if (!isAttachmentArray(value)) {
    return <span className="text-zinc-400 italic">No attachments</span>;
  }

  const attachments: AttachedDocument[] = value;

  return (
    <div className="space-y-1">
      {attachments.map((doc) => (
        <div key={doc.id} className="flex items-center gap-1.5 text-zinc-700">
          <FileText className="h-3.5 w-3.5 text-zinc-400" />
          <span
            className="text-sm truncate max-w-[200px]"
            title={doc.filename}
          >
            {doc.filename}
          </span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Main Field Value Renderer
// ============================================================================

/**
 * Main field value renderer component.
 * Renders the appropriate UI based on field type.
 */
export const FieldValue: React.FC<FieldValueProps> = ({
  value,
  fieldType,
  className,
}) => {
  const content = (() => {
    switch (fieldType) {
      case CustomFieldType.INTEGER:
        return <TextValue value={value} />;
      case CustomFieldType.CURRENCY:
        return <CurrencyValue value={value} />;
      case CustomFieldType.TEXT:
        return <TextValue value={value} />;
      case CustomFieldType.DATE:
      case CustomFieldType.DEADLINE:
        return <DateValue value={value} />;
      case CustomFieldType.DATETIME:
        return <DateTimeValue value={value} />;
      case CustomFieldType.BOOLEAN:
        return <BooleanValue value={value} />;
      case CustomFieldType.MULTILINE_TEXT:
        return <MultilineTextValue value={value} />;
      case CustomFieldType.PERSON:
        return <PersonValue value={value} />;
      case CustomFieldType.DROPDOWN:
        return <DropdownValue value={value} />;
      case CustomFieldType.MULTI_SELECT:
        return <MultiSelectValue value={value} />;
      case CustomFieldType.STRING_LIST:
        return <StringListValue value={value} />;
      case CustomFieldType.PERCENTAGE:
        return <PercentageValue value={value} />;
      case CustomFieldType.PROJECT_LINK:
        return <ProjectLinkValue value={value} />;
      case CustomFieldType.ATTACHMENT:
      case CustomFieldType.DOCUMENT:
        return <AttachmentValue value={value} />;
      case CustomFieldType.DOC_GEN_BUTTON:
      case CustomFieldType.DOC_GEN_AI_BUTTON:
        // Doc gen buttons are not rendered as values
        return <span className="text-zinc-400 italic">Document Generator</span>;
      default:
        // Check if it's a computed field based on structure (for fields without explicit type)
        if (isComputedValue(value)) {
          return <ComputedValueRenderer value={value} />;
        }
        return <TextValue value={value} />;
    }
  })();

  return <div className={cn('text-sm', className)}>{content}</div>;
};

export default FieldValue;
